import { taskRailRoutes } from '@modules/Tasks/Resources/js/utils/taskRailRoutes';

/**
 * Send a project being opened to the tab the organization chose (Settings ▸
 * Operations ▸ Projects). The overview is where a project lands when nothing was
 * said, so this guard sits on the overview's own route: it is the screen the
 * bare `/projects/:id` resolves to.
 *
 * Only when the project is being *opened* — arriving from its own Overview tab,
 * clicked in the rail, must stay on the Overview, or a default of « Tâches »
 * would make the first tab unreachable. Same project, already inside it: leave
 * it alone. Another project, or anywhere else: this is an opening.
 *
 * The settings are awaited rather than read optimistically: the guard runs
 * before the first paint, and a cold load would otherwise always land on the
 * overview and only obey the setting from the second project on.
 */
async function openOnDefaultTab(to, from) {
    if (from?.params?.id && String(from.params.id) === String(to.params.id)) {
        return true;
    }

    const { useOrgStore } = await import('@/stores/org');
    const settings = await useOrgStore().loadProjectSettings();
    const tab = settings?.default_tab;

    if (!tab || tab === 'overview') {
        return true;
    }

    // Imported here rather than at the top: this file is loaded from the router's
    // own module list, and the tab map reaches back to the router.
    const [{ TAB_KEY_TO_ROUTE }, { default: router }] = await Promise.all([
        import('@/composables/useObjectTabLabels'),
        import('@/routes')
    ]);

    const name = `project-${TAB_KEY_TO_ROUTE[tab] ?? tab}`;

    // A tab whose screen was retired outlives the setting that named it; landing
    // on the overview is the mild outcome, and the right one.
    return router.hasRoute(name) ? { name, params: to.params, query: to.query } : true;
}

export default {
    slug: 'projects',
    ability: 'projects',
    routes: [
        {
            // Opérations ▸ Ressources: the team's planned load across every
            // project, read from the tasks' estimates, assignees and dates.
            // The rail opens over it like over the board, so a task behind a
            // number is read and replanned without leaving the weeks.
            path: 'resources',
            name: 'project-resources',
            component: () => import('@modules/Projects/Resources/js/pages/resources/Resources.vue'),
            children: taskRailRoutes('project-resources')
        },
        {
            path: 'projects',
            name: 'project-list',
            component: () => import('@modules/Projects/Resources/js/pages/List.vue'),
            // What the list asks for as it opens, in one request (utils/listBootstrap.js);
            // `project` is the layout the records opened inside it read.
            meta: { listBootstrap: { list: 'projects', object: 'project' } },
            children: [
                // The portfolio timeline opens a task over the frieze, in the
                // rail, rather than leaving for the project's own Gantt.
                ...taskRailRoutes('project-list'),
                {
                    path: ':id(\\d+)',
                    name: 'project-preview',
                    component: () => import('@modules/Projects/Resources/js/pages/Preview.vue'),
                    redirect: { name: 'project-overview' },
                    props: true,
                    children: [
                        {
                            path: '/organization/:org/projects/:id(\\d+)',
                            name: 'project-overview',
                            component: () => import('@modules/Projects/Resources/js/pages/preview/Overview.vue'),
                            props: true,
                            beforeEnter: openOnDefaultTab
                        },
                        {
                            path: 'timesheets',
                            name: 'project-timesheets',
                            component: () => import('@modules/Projects/Resources/js/pages/preview/TabTimesheets.vue'),
                            props: true
                        },
                        {
                            path: 'activities',
                            name: 'project-activities',
                            component: () => import('@/components/base/tabs/TabActivities.vue'),
                            props: true
                        },
                        {
                            // The same module a contact has: the project's own
                            // links, and underneath, read-only, the links of
                            // whatever is filed under it.
                            path: 'shortcuts',
                            name: 'project-shortcuts',
                            component: () => import('@/components/base/tabs/TabShortcuts.vue'),
                            props: true
                        },
                        {
                            // What was observed on the project: the same screen
                            // a work order has, pointed at a project. No visits
                            // to file a report under, so the component is
                            // reached directly rather than through a wrapper.
                            path: 'reports',
                            name: 'project-reports',
                            component: () => import('@/components/base/tabs/TabReports.vue'),
                            props: true
                        },
                        {
                            // What replaced the comment thread: dated
                            // statements carrying a state, a progress and the
                            // hours they were made with.
                            path: 'updates',
                            name: 'project-updates',
                            component: () => import('@modules/Projects/Resources/js/pages/preview/Updates.vue'),
                            props: true
                        },
                        {
                            // A project's tasks have their own screen — board
                            // and list over one search surface — rather than
                            // the generic app-tasks table every other preview
                            // uses.
                            path: 'tasks',
                            name: 'project-tasks',
                            component: () => import('@modules/Projects/Resources/js/pages/preview/tasks/Tasks.vue'),
                            props: true,
                            children: [
                                {
                                    // Creating a task is the same panel, in the
                                    // state it already had for editing — the
                                    // rail is where a task is read, changed and
                                    // now written, instead of a window that
                                    // showed different fields from the screen
                                    // the person would land on.
                                    //
                                    // Declared before the numeric one for the
                                    // reader's sake; a static segment wins over
                                    // a parameter whatever the order.
                                    path: 'new',
                                    name: 'project-task-new',
                                    component: () =>
                                        import('@modules/Tasks/Resources/js/components/tasks/TaskPanel.vue'),
                                    props: { create: true }
                                },
                                {
                                    // Nested, so opening a task slides a panel over
                                    // the board rather than replacing it: the column
                                    // a card came from stays on screen behind it, and
                                    // closing costs no reload.
                                    path: ':taskId(\\d+)',
                                    name: 'project-task-detail',
                                    component: () =>
                                        import('@modules/Tasks/Resources/js/components/tasks/TaskPanel.vue'),
                                    props: true
                                }
                            ]
                        },
                        {
                            // The work breakdown: the plan's rows as a table.
                            // Same rule as the timeline below — the route ships
                            // with the tab, or buildTabs() drops it silently.
                            path: 'wbs',
                            name: 'project-wbs',
                            component: () => import('@modules/Projects/Resources/js/pages/preview/plan/Plan.vue'),
                            props: true,
                            children: [
                                {
                                    path: 'tasks/new',
                                    name: 'project-wbs-task-new',
                                    component: () =>
                                        import('@modules/Tasks/Resources/js/components/tasks/TaskPanel.vue'),
                                    props: { create: true }
                                },
                                {
                                    path: 'tasks/:taskId(\\d+)',
                                    name: 'project-wbs-task-detail',
                                    component: () =>
                                        import('@modules/Tasks/Resources/js/components/tasks/TaskPanel.vue'),
                                    props: true
                                }
                            ]
                        },
                        {
                            // The timeline. Its route has to exist in the same
                            // change that declares the tab: buildTabs() drops a
                            // tab whose route the router does not know, so a
                            // declared-but-unrouted « Plan » vanishes silently.
                            path: 'plan',
                            name: 'project-plan',
                            component: () => import('@modules/Projects/Resources/js/pages/preview/gantt/Gantt.vue'),
                            props: true,
                            children: [
                                {
                                    // The plan opens the same panel the board
                                    // does, under its own route: a task clicked
                                    // on the frieze has to slide over the
                                    // frieze. Routed through the tasks tab
                                    // instead, closing the panel would leave
                                    // the reader on a screen they never asked
                                    // for — and the bar they were looking at
                                    // would be gone.
                                    path: 'tasks/new',
                                    name: 'project-plan-task-new',
                                    component: () =>
                                        import('@modules/Tasks/Resources/js/components/tasks/TaskPanel.vue'),
                                    props: { create: true }
                                },
                                {
                                    path: 'tasks/:taskId(\\d+)',
                                    name: 'project-plan-task-detail',
                                    component: () =>
                                        import('@modules/Tasks/Resources/js/components/tasks/TaskPanel.vue'),
                                    props: true
                                }
                            ]
                        },
                        {
                            path: 'dashboard',
                            name: 'project-dashboard',
                            component: () => import('@modules/Projects/Resources/js/pages/preview/Dashboard.vue'),
                            props: true
                        },
                        {
                            path: 'milestones',
                            name: 'project-milestones',
                            component: () => import('@modules/Projects/Resources/js/pages/preview/Milestones.vue'),
                            props: true,
                            children: [
                                {
                                    // Its own panel routes, like the board's and
                                    // the plan's: a milestone is a task, so it is
                                    // read and written in the same rail — and
                                    // routed here rather than through the tasks
                                    // tab, so closing it returns to the card
                                    // that was clicked instead of a screen the
                                    // reader never asked for.
                                    path: 'tasks/new',
                                    name: 'project-milestone-task-new',
                                    component: () =>
                                        import('@modules/Tasks/Resources/js/components/tasks/TaskPanel.vue'),
                                    props: { create: true }
                                },
                                {
                                    path: 'tasks/:taskId(\\d+)',
                                    name: 'project-milestone-task-detail',
                                    component: () =>
                                        import('@modules/Tasks/Resources/js/components/tasks/TaskPanel.vue'),
                                    props: true
                                }
                            ]
                        },
                        // The money, in the order it is run: what the project
                        // was sold for, what it has spent, what has been
                        // billed. The three screens a work order has, asked of
                        // a project — which is what a project is once you stop
                        // looking at its board.
                        {
                            path: 'budget',
                            name: 'project-budget',
                            component: () => import('@modules/Projects/Resources/js/pages/preview/finance/Budget.vue'),
                            props: true
                        },
                        {
                            path: 'purchases',
                            name: 'project-purchases',
                            component: () =>
                                import('@modules/Projects/Resources/js/pages/preview/finance/Purchases.vue'),
                            props: true,
                            // `plan` opens the material plan: the tab's own key, not a list filter.
                            meta: { tabQuery: ['plan'] }
                        },
                        {
                            path: 'billing',
                            name: 'project-billing',
                            component: () => import('@modules/Projects/Resources/js/pages/preview/finance/Billing.vue'),
                            props: true
                        },
                        {
                            // A milestone is a task, so its record is the task
                            // panel — one screen instead of two that had to be
                            // kept saying the same thing. Kept as a redirect so
                            // links already sent out, bookmarked or pasted into
                            // an email still land somewhere.
                            path: 'milestones/:milestoneId(\\d+)',
                            name: 'project-milestone-detail',
                            redirect: to => ({
                                name: 'project-task-detail',
                                params: { ...to.params, taskId: to.params.milestoneId }
                            })
                        },
                        {
                            path: 'members',
                            name: 'project-members',
                            component: () => import('@modules/Projects/Resources/js/pages/preview/Members.vue'),
                            props: true
                        },
                        {
                            // Its own page rather than the generic TabLogs: a
                            // project's history is read beside the week's
                            // figures, its authors and what has gone quiet.
                            path: 'logs',
                            name: 'project-logs',
                            component: () => import('@modules/Projects/Resources/js/pages/preview/Logs.vue'),
                            props: true
                        },
                        {
                            path: 'documents/:docPath(.*)*',
                            name: 'project-documents',
                            component: () => import('@/components/base/tabs/TabDocuments.vue'),
                            props: true
                        },
                        {
                            path: 'document-requests',
                            name: 'project-document-requests',
                            component: () => import('@/components/base/tabs/TabDocumentRequests.vue'),
                            props: true
                        },
                        {
                            path: 'contracts',
                            name: 'project-contracts',
                            component: () => import('@/components/base/tabs/TabContracts.vue'),
                            props: true
                        },
                        {
                            // A tab the organization added to the project
                            // layout. Without this route the rail dropped every
                            // one of them, and adding a project tab in the
                            // Object Manager did nothing at all.
                            path: 'ct:tabId(\\d+)',
                            name: 'project-custom-tab',
                            component: () => import('@/components/base/tabs/TabCustomLayout.vue'),
                            props: true
                        },
                        {
                            // The project's own arrangement: which tabs it shows,
                            // which columns its board has.
                            path: 'configuration',
                            name: 'project-configuration',
                            component: () => import('@modules/Projects/Resources/js/pages/preview/Configuration.vue'),
                            props: true
                        }
                    ]
                }
            ]
        }
    ],
    // The project taxonomy sits beside the work order types, under Settings ▸
    // Operations: both answer "what kinds of work does this organization do".
    // `project-categories`, not `projects` — the work settings route already
    // claims that alias.
    operationsRoutes: [
        {
            path: 'project-settings',
            name: 'settings-operations-projects',
            component: () => import('@modules/Projects/Resources/js/pages/settings/ProjectSettings.vue')
        },
        {
            path: 'project-categories',
            name: 'settings-operations-project-categories',
            component: () => import('@modules/Projects/Resources/js/pages/settings/ProjectCategorySettings.vue')
        },
        {
            path: 'project-templates',
            name: 'settings-operations-project-templates',
            component: () => import('@modules/Projects/Resources/js/pages/settings/ProjectTemplateSettings.vue')
        },
        {
            // `new`, or a template's id.
            path: 'project-templates/:templateId(new|\\d+)',
            name: 'settings-operations-project-template-edit',
            component: () => import('@modules/Projects/Resources/js/pages/settings/ProjectTemplateEdit.vue'),
            props: true
        }
    ],
    portalRoutes: [
        {
            path: 'projects',
            name: 'portal-projects',
            component: () => import('@modules/Projects/Resources/js/portal/Projects.vue')
        },
        {
            path: 'projects/:id(\\d+)',
            name: 'portal-project-detail',
            component: () => import('@modules/Projects/Resources/js/portal/ProjectDetail.vue'),
            props: true
        },
        {
            path: 'time-banks',
            name: 'portal-time-banks',
            component: () => import('@modules/Projects/Resources/js/portal/TimeBanks.vue')
        }
    ],
    nav: () => ({
        section: 'projects',
        items: [
            {
                title: i18n.t('Projects'),
                icon: 'format-list-bulleted',
                to: { name: 'project-list' }
            },
            {
                // Everybody's weeks are on it: for those who read every
                // project, as the endpoint requires.
                title: i18n.t('Resources'),
                icon: 'account-group-outline',
                to: { name: 'project-resources' },
                visible: orgStore => orgStore.can('projects.view')
            }
        ]
    })
};
